import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FRIENDLY_FIELDS,
  SEX_OPTIONS,
  SPECIES_OPTIONS,
  fieldMeta,
  newConditionRow,
  predicateJsonToRows,
  rowsToPredicateJson,
  summarizePredicate,
  validateRows,
  withFieldChanged,
  type ConditionRow,
  type FriendlyField,
  type FriendlyOp,
  type Inclusive,
} from './predicate-builder';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const PRESET_PREDICATES: { label: string; json: string }[] = [
  { label: 'Especie = perro', json: JSON.stringify({ op: 'eq', field: 'pet.species', value: 'dog' }, null, 2) },
  { label: 'Especie = gato', json: JSON.stringify({ op: 'eq', field: 'pet.species', value: 'cat' }, null, 2) },
  {
    label: 'Perro 1–5 kg',
    json: JSON.stringify(
      {
        op: 'and',
        clauses: [
          { op: 'eq', field: 'pet.species', value: 'dog' },
          { op: 'between', field: 'pet.current_weight_grams', min: 1000, max: 5000 },
        ],
      },
      null,
      2,
    ),
  },
  {
    label: 'Perro 5–10 kg',
    json: JSON.stringify(
      {
        op: 'and',
        clauses: [
          { op: 'eq', field: 'pet.species', value: 'dog' },
          { op: 'between', field: 'pet.current_weight_grams', min: 5000, max: 10000, inclusive: 'max' },
        ],
      },
      null,
      2,
    ),
  },
];

interface PredicateBuilderProps {
  value: string;
  onChange: (predicate_json: string) => void;
}

export function PredicateBuilder({ value, onChange }: PredicateBuilderProps): JSX.Element {
  const { t } = useTranslation();
  const initial_rows = predicateJsonToRows(value);
  const [mode, setMode] = useState<'builder' | 'advanced'>(initial_rows === null ? 'advanced' : 'builder');
  const [rows, setRows] = useState<ConditionRow[]>(initial_rows ?? []);
  const [advanced_text, setAdvancedText] = useState<string>(value);
  const [switch_error, setSwitchError] = useState<string | null>(null);

  const commitRows = (next: ConditionRow[]): void => {
    setRows(next);
    onChange(rowsToPredicateJson(next));
  };

  const onAddRow = (): void => commitRows([...rows, newConditionRow()]);
  const onRemoveRow = (id: string): void => commitRows(rows.filter((r) => r.id !== id));
  const onUpdateRow = (id: string, patch: Partial<ConditionRow>): void =>
    commitRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const onSwitchToAdvanced = (): void => {
    setAdvancedText(value);
    setMode('advanced');
    setSwitchError(null);
  };

  const onSwitchToBuilder = (): void => {
    const parsed = predicateJsonToRows(advanced_text);
    if (parsed === null) {
      setSwitchError(t('pricing.error_cannot_simplify'));
      return;
    }
    setRows(parsed);
    onChange(rowsToPredicateJson(parsed));
    setMode('builder');
    setSwitchError(null);
  };

  const row_error = mode === 'builder' ? validateRows(rows) : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{t('pricing.predicate')}</Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            if (mode === 'builder') onSwitchToAdvanced();
            else onSwitchToBuilder();
          }}
        >
          {mode === 'builder' ? t('pricing.mode_advanced') : t('pricing.mode_builder')}
        </Button>
      </div>

      {mode === 'builder' ? (
        <div className="space-y-2">
          {rows.length === 0 ? (
            <p className="rounded-md border border-dashed border-input p-3 text-xs text-muted-foreground">
              {t('pricing.summary_always')} — {t('pricing.no_conditions_hint')}
            </p>
          ) : null}
          {rows.map((row) => (
            <ConditionRowEditor
              key={row.id}
              row={row}
              onChange={(patch) => onUpdateRow(row.id, patch)}
              onRemove={() => onRemoveRow(row.id)}
            />
          ))}
          <Button type="button" size="sm" variant="outline" onClick={onAddRow}>
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('pricing.add_condition')}</span>
          </Button>
          <p className="text-xs text-muted-foreground">
            <strong>{t('pricing.summary_label')}: </strong>
            {summarizePredicate(rowsToPredicateJson(rows), t)}
          </p>
          {row_error ? <p className="text-xs text-destructive">{t(row_error)}</p> : null}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {PRESET_PREDICATES.map((p) => (
              <Button
                key={p.label}
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAdvancedText(p.json);
                  onChange(p.json);
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <textarea
            className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
            value={advanced_text}
            onChange={(e) => {
              setAdvancedText(e.target.value);
              onChange(e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">{t('pricing.predicate_hint')}</p>
          {switch_error ? <p className="text-xs text-destructive">{switch_error}</p> : null}
        </div>
      )}
    </div>
  );
}

function ConditionRowEditor({
  row,
  onChange,
  onRemove,
}: {
  row: ConditionRow;
  onChange: (patch: Partial<ConditionRow>) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const meta = fieldMeta(row.field);

  return (
    <div className="grid gap-2 rounded-md border border-input p-2 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
      <div className="space-y-1">
        <Label className="text-xs">{t('pricing.condition_field')}</Label>
        <select
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          value={row.field}
          onChange={(e) => onChange(withFieldChanged(row, e.target.value as FriendlyField))}
        >
          {FRIENDLY_FIELDS.map((f) => (
            <option key={f.field} value={f.field}>
              {t(f.label_key)}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t('pricing.condition_op')}</Label>
        <select
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          value={row.op}
          onChange={(e) => onChange({ op: e.target.value as FriendlyOp })}
        >
          {meta.ops.map((op) => (
            <option key={op} value={op}>
              {t(`pricing.summary_op_${op}`)}
            </option>
          ))}
        </select>
      </div>
      <ValueEditor row={row} onChange={onChange} />
      <Button type="button" size="sm" variant="ghost" onClick={onRemove} aria-label={t('common.remove')}>
        <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
      </Button>
    </div>
  );
}

function ValueEditor({
  row,
  onChange,
}: {
  row: ConditionRow;
  onChange: (patch: Partial<ConditionRow>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const meta = fieldMeta(row.field);

  if (row.op === 'between') {
    return (
      <div className="grid grid-cols-2 gap-1 md:col-span-1">
        <div className="space-y-1">
          <Label className="text-xs">{t('pricing.condition_min')}</Label>
          <Input
            inputMode="decimal"
            value={row.min}
            onChange={(e) => onChange({ min: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('pricing.condition_max')}</Label>
          <Input
            inputMode="decimal"
            value={row.max}
            onChange={(e) => onChange({ max: e.target.value })}
          />
        </div>
        <div className="col-span-2 space-y-1">
          <Label className="text-xs">{t('pricing.condition_inclusive')}</Label>
          <select
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
            value={row.inclusive}
            onChange={(e) => onChange({ inclusive: e.target.value as Inclusive })}
          >
            <option value="both">{t('pricing.inclusive_both')}</option>
            <option value="min">{t('pricing.inclusive_min')}</option>
            <option value="max">{t('pricing.inclusive_max')}</option>
            <option value="none">{t('pricing.inclusive_none')}</option>
          </select>
        </div>
      </div>
    );
  }

  const label = t('pricing.condition_value');

  if (meta.value_kind === 'species') {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{label}</Label>
        <select
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          value={row.value}
          onChange={(e) => onChange({ value: e.target.value })}
        >
          {SPECIES_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.label_key)}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (meta.value_kind === 'sex') {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{label}</Label>
        <select
          className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          value={row.value}
          onChange={(e) => onChange({ value: e.target.value })}
        >
          {SEX_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.label_key)}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {label}
        {meta.value_kind === 'number_kg' ? ' (kg)' : ''}
      </Label>
      <Input
        inputMode={meta.value_kind === 'text' ? 'text' : 'decimal'}
        value={row.value}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    </div>
  );
}
